<template>
    <div class="modal-overlay">
        <div class="modal-content">
            <h2>Update Goal Progress</h2>

            <!-- Read-only Goal Summary Section -->
            <div class="goal-summary">
                <div class="goal-header">
                    <div class="goal-type-pill" :class="`type-${goal.goal_type_id}`">
                        {{ getGoalTypeName(goal.goal_type_id) }}
                    </div>
                    <h3>{{ goal.title }}</h3>
                </div>
                <p class="goal-description">{{ goal.description }}</p>
                <div class="goal-metrics">
                    <div class="metric">
                        <label>Target Value:</label>
                        <span>{{ goal.target_value }}</span>
                    </div>
                    <div class="metric">
                        <label>Due Date:</label>
                        <span>{{ formatDate(goal.target_date) }}</span>
                    </div>
                </div>

                <!-- Last Update Section -->
                <div class="last-update" v-if="lastUpdate">
                    <div class="divider"></div>
                    <div class="last-update-grid">
                        <div class="metric">
                            <label>Last Progress Value:</label>
                            <span>{{ lastUpdate.current_value }}</span>
                        </div>
                        <div class="metric">
                            <label>Updated On:</label>
                            <span>{{ formatDate(lastUpdate.update_date) }}</span>
                        </div>
                    </div>
                    <div class="last-update-notes" v-if="lastUpdate.notes">
                        <label>Last Update Notes:</label>
                        <p>{{ lastUpdate.notes }}</p>
                    </div>
                </div>
            </div>

            <!-- Update Form Section -->
            <form @submit.prevent="handleSubmit" class="update-form">
                <div class="form-group">
                    <label>Update Date</label>
                    <input 
                        type="date" 
                        v-model="form.update_date"
                    />
                </div>

                <div class="form-group">
                    <label>Current Progress Value</label>
                    <input 
                        type="number" 
                        v-model="form.current_value"
                        step="0.01"
                    />
                    <div v-if="progressPercentage !== null" class="progress-indicator">
                        {{ progressPercentage }}% Complete
                    </div>
                </div>

                <div class="form-group">
                    <label for="notes">Update Notes</label>
                    <textarea 
                        id="notes"
                        v-model="form.notes"
                        rows="3"
                        required
                        placeholder="Describe your progress..."
                    ></textarea>
                </div>

                <div class="modal-actions">
                    <button type="button" class="cancel-btn" @click="$emit('close')">
                        Cancel
                    </button>
                    <button type="submit" class="save-btn">
                        Save Update
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { format } from 'date-fns';
import { goalService } from '../services/goalService';
import confetti from 'canvas-confetti';

export default {
    name: 'GoalUpdateModal',
    props: {
        goal: {
            type: Object,
            required: true
        },
        goalTypes: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'update'],
    
    setup(props, { emit }) {
        const form = ref({
            update_date: format(new Date(), 'yyyy-MM-dd'),
            current_value: '',
            notes: ''
        });

        const progressPercentage = computed(() => {
            if (!form.value.current_value || !props.goal.target_value) return null;
            return Math.round((form.value.current_value / props.goal.target_value) * 100);
        });

        const getCurrentDate = () => {
            return format(new Date(), 'yyyy-MM-dd');
        };

        const formatDate = (date) => {
            return format(new Date(date), 'MMM dd, yyyy');
        };

        const getGoalTypeName = (typeId) => {
            return props.goalTypes[typeId] || 'Unknown Type';
        };

        const handleSubmit = () => {
            const updateData = {
                goal_id: props.goal.id,
                update_date: form.value.update_date,
                notes: form.value.notes,
                current_value: form.value.current_value || null
            };

            // Check if goal is being completed
            if (form.value.current_value >= props.goal.target_value) {
                // Don't trigger if user prefers reduced motion
                if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    confetti({
                        particleCount: 200,
                        spread: 100,
                        origin: { y: 0.6 },
                        colors: ['#FFB3BA', '#BAFFC9', '#BAE1FF', '#FFFFBA'],  // Pastel pink, green, blue, and yellow
                        disableForReducedMotion: true,
                        // Adjust particle count for mobile
                        ...(window.innerWidth < 600 ? { particleCount: 100, spread: 55 } : {})
                    });
                }
            }

            emit('update', updateData);
        };

        const lastUpdate = ref(null);
        
        const fetchLastUpdate = async () => {
            try {
                const updates = await goalService.getGoalUpdates(props.goal.id);
                updates.sort((a, b) => {
                    const dateA = new Date(a.update_date);
                    const dateB = new Date(b.update_date);
                    if (dateA.getTime() === dateB.getTime()) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    }
                    return dateB - dateA;
                });
                
                if (updates && updates.length > 0) {
                    lastUpdate.value = updates[0];
                }
            } catch (error) {
                console.error('Error fetching last update:', error);
            }
        };
        
        onMounted(() => {
            fetchLastUpdate();
        });

        return {
            form,
            progressPercentage,
            getCurrentDate,
            formatDate,
            getGoalTypeName,
            handleSubmit,
            lastUpdate
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 24px;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
}

.goal-summary {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 24px;
}

.goal-header {
    display: flex;
    flex-direction: row-reverse;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
    position: relative;
}

.goal-type-pill {
    position: absolute;
    right: 0;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.9rem;
}

.goal-header h3 {
    margin: 0 auto;
    padding: 0 60px;
}

.goal-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 16px;
}

.metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.metric label {
    font-size: 0.9rem;
    color: #666;
}

.form-group {
    margin-bottom: 16px;
}

.progress-indicator {
    margin-top: 8px;
    color: #2196f3;
    font-weight: 500;
}

/* Type-specific styles */
.type-1 { background: #e3f2fd; color: #1976d2; }
.type-2 { background: #e8f5e9; color: #2e7d32; }
.type-3 { background: #fff3e0; color: #f57c00; }
.type-4 { background: #fbe9e7; color: #d32f2f; }

/* Form controls */
input, textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
}

.cancel-btn {
    padding: 8px 16px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn {
    padding: 8px 16px;
    background: #4285f4;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn:hover {
    background: #1976d2;
}

.divider {
    height: 1px;
    background: #e0e0e0;
    margin: 16px 0;
}

.last-update {
    margin-top: 16px;
}

.last-update-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.last-update-notes {
    margin-top: 12px;
}

.last-update-notes label {
    font-size: 0.9rem;
    color: #666;
    display: block;
    margin-bottom: 4px;
}

.last-update-notes p {
    margin: 0;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 0.9rem;
}
</style> 